import { randomUUID } from 'crypto';
import { logger } from '../utils/logger';
import { STORAGE_CONFIG } from '../config/storage';
import { Action } from './action';

/**
 * UIBlock represents a single user and assistant message block in a thread
 * Contains user question, component metadata, component data, text response, and available actions
 */
export class UIBlock {
  private id: string;
  private userQuestion: string;
  private generatedComponentMetadata: Record<string, any>;
  private componentData: Record<string, any>;
  private textResponse: string | null;
  private actions: Action[] | null | Promise<Action[]>;
  private createdAt: Date;

  /**
   * Creates a new UIBlock instance
   * @param userQuestion - The user's question or input
   * @param componentData - The component data object
   * @param generatedComponentMetadata - Optional metadata about the generated component
   * @param actions - Optional array of available actions
   * @param id - Optional custom ID, generates UUID if not provided
   * @param textResponse - Optional text response from LLM
   */
  constructor(
    userQuestion: string,
    componentData: Record<string, any> = {},
    generatedComponentMetadata: Record<string, any> = {},
    actions: Action[] = [],
    id?: string,
    textResponse: string | null = null
  ) {
    this.id = id || randomUUID();
    this.userQuestion = userQuestion;
    this.componentData = componentData;
    this.generatedComponentMetadata = generatedComponentMetadata;
    this.actions = actions;
    this.textResponse = textResponse;
    this.createdAt = new Date();
  }

  /**
   * Get the UIBlock ID
   */
  getId(): string {
    return this.id;
  }

  /**
   * Get the user question
   */
  getUserQuestion(): string {
    return this.userQuestion;
  }

  /**
   * Set or update the user question
   */
  setUserQuestion(question: string): void {
    this.userQuestion = question;
  }

  /**
   * Get component metadata
   */
  getComponentMetadata(): Record<string, any> {
    return this.generatedComponentMetadata;
  }

  getTextResponse(): string {
    return this.textResponse || '';
  }

  /**
   * Set or update component metadata
   */
  setComponentMetadata(metadata: Record<string, any>): void {
    this.generatedComponentMetadata = { ...this.generatedComponentMetadata, ...metadata };
  }

  /**
   * Get component data
   */
  getComponentData(): Record<string, any> {
    return this.componentData;
  }

  /**
   * Calculate size of data in bytes
   */
  private getDataSizeInBytes(data: any): number {
    try {
      const jsonString = JSON.stringify(data);
      return Buffer.byteLength(jsonString, 'utf8');
    } catch (error) {
      logger.error('Error calculating data size:', error);
      return 0;
    }
  }

  /**
   * Limit array data to maximum rows
   */
  private limitArrayData(data: any[]): { data: any[]; metadata: any } {
    const totalRows = data.length;
    const limitedData = data.slice(0, STORAGE_CONFIG.MAX_ROWS_PER_BLOCK);

    return {
      data: limitedData,
      metadata: {
        totalRows,
        storedRows: limitedData.length,
        isTruncated: totalRows > STORAGE_CONFIG.MAX_ROWS_PER_BLOCK,
      },
    };
  }

  /**
   * Check if data exceeds size limit
   */
  private exceedsSizeLimit(data: any): boolean {
    const size = this.getDataSizeInBytes(data);
    return size > STORAGE_CONFIG.MAX_SIZE_PER_BLOCK_BYTES;
  }

  /**
   * Process and limit data before storing
   */
  private processDataForStorage(data: any): any {
    // If data is an array, limit rows
    if (Array.isArray(data)) {
      const { data: limitedData, metadata } = this.limitArrayData(data);

      // Check size after limiting rows
      const size = this.getDataSizeInBytes(limitedData);

      logger.info(
        `UIBlock ${this.id}: Storing ${metadata.storedRows}/${metadata.totalRows} rows (${(size / 1024).toFixed(2)} KB)`
      );

      // If still too large, store only metadata
      if (this.exceedsSizeLimit(limitedData)) {
        logger.warn(
          `UIBlock ${this.id}: Data too large (${(size / 1024 / 1024).toFixed(2)} MB), storing metadata only`
        );
        return {
          ...metadata,
          preview: limitedData.slice(0, 3), // Store only first 3 rows as preview
          dataTooLarge: true,
        };
      }

      return {
        data: limitedData,
        ...metadata,
      };
    }

    // For non-array data, check size
    const size = this.getDataSizeInBytes(data);

    if (this.exceedsSizeLimit(data)) {
      logger.warn(
        `UIBlock ${this.id}: Data too large (${(size / 1024 / 1024).toFixed(2)} MB), storing summary only`
      );
      return {
        dataTooLarge: true,
        dataType: typeof data,
        keys: typeof data === 'object' ? Object.keys(data) : undefined,
      };
    }

    return data;
  }

  /**
   * Set or update component data with size and row limits
   */
  setComponentData(data: Record<string, any>): void {
    const processedData = this.processDataForStorage(data);
    this.componentData = { ...this.componentData, ...processedData };
  }



  /**
   * Set or update text response
   */
  setTextResponse(textResponse: string | null): void {
    this.textResponse = textResponse;
  }

  /**
   * Get all actions (only if they are resolved, not if fetching)
   */
  getActions(): Action[] | null | Promise<Action[]> {
    return this.actions;
  }

  /**
   * Get or fetch actions
   * If actions don't exist or are a Promise, calls the generateFn and stores the promise
   * If actions already exist, returns them
   * @param generateFn - Async function to generate actions
   * @returns Promise resolving to Action[]
   */
  async getOrFetchActions(generateFn: () => Promise<Action[]>): Promise<Action[]> {
    // If actions already exist and are not a Promise, return them

    if (this.actions && !(this.actions instanceof Promise) && Array.isArray(this.actions) && this.actions.length > 0) {
      return this.actions;
    }

    // If already fetching, cancel and start new fetch
    // Set new promise for fetching
    const fetchPromise = generateFn();
    this.actions = fetchPromise;

    try {
      // Wait for the promise to resolve
      const resolvedActions = await fetchPromise;
      // Store the resolved actions
      logger.info(`Fetched ${resolvedActions.length} actions for UIBlock: ${this.id}`);
      this.actions = resolvedActions;
      return resolvedActions;
    } catch (error) {
      // If generation fails, reset to null
      this.actions = null;
      throw error;
    }
  }

  /**
   * Set or replace all actions
   */
  setActions(actions: Action[]): void {
    this.actions = actions;
  }

  /**
   * Add a single action (only if actions are resolved)
   */
  addAction(action: Action): void {
    if (this.actions && Array.isArray(this.actions)) {
      this.actions.push(action);
    }
  }

  /**
   * Add multiple actions (only if actions are resolved)
   */
  addActions(actions: Action[]): void {
    if (this.actions && Array.isArray(this.actions)) {
      this.actions.push(...actions);
    }
  }

  /**
   * Remove an action by ID (only if actions are resolved)
   */
  removeAction(actionId: string): boolean {
    if (this.actions && Array.isArray(this.actions)) {
      const index = this.actions.findIndex(a => a.id === actionId);
      if (index > -1) {
        this.actions.splice(index, 1);
        return true;
      }
    }
    return false;
  }

  /**
   * Clear all actions
   */
  clearActions(): void {
    this.actions = null;
  }

  /**
   * Get creation timestamp
   */
  getCreatedAt(): Date {
    return this.createdAt;
  }

  /**
   * Convert UIBlock to JSON-serializable object
   */
  toJSON(): Record<string, any> {
    // Handle Promise case for serialization
    let actionsValue: Action[] | null = null;
    if (this.actions && !(this.actions instanceof Promise) && Array.isArray(this.actions)) {
      actionsValue = this.actions;
    }

    return {
      id: this.id,
      userQuestion: this.userQuestion,
      generatedComponentMetadata: this.generatedComponentMetadata,
      componentData: this.componentData,
      textResponse: this.textResponse,
      actions: actionsValue,
      isFetchingActions: this.actions instanceof Promise,
      createdAt: this.createdAt.toISOString(),
    };
  }
}
